/**
 * キャンペーン管理（campaigns）の hooks。
 * 仕様書: docs/01_spec/spec_line_bot.md A-11
 *
 * 無限ループ防止:
 *   - 全 write は safeAddDoc / safeUpdateDoc / safeDeleteDoc 経由
 *   - onSnapshot 監視は使わず useQuery + invalidateQueries で更新
 *   - ボード上のドラッグ移動は呼び出し側で debounce してから update
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';

import { safeAddDoc, safeDeleteDoc, safeUpdateDoc } from '@/lib/safeWrite';

import type { Campaign } from '@goenhub/shared';
import { createEmptyBackgroundInfo } from '@goenhub/shared';

import { db } from '@/lib/firebase';

export interface CampaignRecord extends Campaign {
  id: string;
}

export function useCampaigns() {
  return useQuery({
    queryKey: ['campaigns'],
    queryFn: async (): Promise<CampaignRecord[]> => {
      const snap = await getDocs(
        query(collection(db, 'campaigns'), orderBy('updatedAt', 'desc')),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Campaign) }));
    },
  });
}

export function useCampaign(id: string | null | undefined) {
  return useQuery({
    queryKey: ['campaigns', id],
    enabled: !!id,
    queryFn: async (): Promise<CampaignRecord | null> => {
      if (!id) return null;
      const snap = await getDoc(doc(db, 'campaigns', id));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as Campaign) };
    },
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<Campaign>): Promise<string> => {
      // background_info の初期化：渡された値を既定キーセットにマージ
      const background_info = {
        ...createEmptyBackgroundInfo(),
        ...(data.background_info ?? {}),
        // 旧フィールドが渡されていれば background_info に流し込み（後方互換）
        ...(data.description !== undefined
          ? { description: data.description }
          : {}),
        ...(data.goal !== undefined ? { goal: data.goal } : {}),
        ...(data.targetSegment !== undefined
          ? { targetSegment: data.targetSegment }
          : {}),
        ...(data.budget !== undefined ? { budget: data.budget } : {}),
        ...(data.kpi !== undefined ? { kpi: data.kpi } : {}),
        ...(data.owner !== undefined ? { owner: data.owner } : {}),
        ...(data.memo !== undefined ? { memo: data.memo } : {}),
      };
      const ref = await safeAddDoc(collection(db, 'campaigns'), {
        name: data.name ?? '無題のキャンペーン',
        status: data.status ?? 'planning',
        elements: data.elements ?? [],
        connections: data.connections ?? [],
        bounds: data.bounds ?? null,
        startAt: data.startAt ?? null,
        endAt: data.endAt ?? null,
        // 新構造（Phase 0）
        background_info,
        // 旧フィールド並走（互換維持、UI 既存読み込みのため）
        description: background_info.description ?? '',
        goal: background_info.goal ?? '',
        targetSegment: background_info.targetSegment ?? '',
        budget: background_info.budget ?? null,
        kpi: background_info.kpi ?? '',
        owner: background_info.owner ?? '',
        memo: background_info.memo ?? '',
        // テンプレート機能
        isTemplate: data.isTemplate ?? false,
        ...(data.fromTemplateId ? { fromTemplateId: data.fromTemplateId } : {}),
        // 新規作成時は休止状態（明示的に「稼働」に切り替えるまで自動発火しない）。
        // 引数で paused が明示されていればそれを優先。
        paused: data.paused ?? true,
        createdBy: data.createdBy ?? '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return ref.id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

interface UpdateCampaignArgs {
  id: string;
  data: Partial<Pick<
    Campaign,
    | 'name'
    | 'status'
    | 'elements'
    | 'connections'
    | 'bounds'
    | 'startAt'
    | 'endAt'
    | 'background_info'
    | 'background_info_schema'
    | 'isTemplate'
    | 'paused'
    // 後方互換（@deprecated）
    | 'description'
    | 'goal'
    | 'targetSegment'
    | 'budget'
    | 'kpi'
    | 'owner'
    | 'memo'
  >> & {
    /**
     * background_info の特定キーだけを部分更新する場合のドット記法ペア。
     * 例: { 'background_info.description': 'new value' }
     * Firestore は dotted keys を sub-field 部分更新として扱う。
     */
    backgroundInfoPatch?: Record<string, unknown>;
  };
}

export function useUpdateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: UpdateCampaignArgs) => {
      const { backgroundInfoPatch, ...rest } = data;
      const payload: Record<string, unknown> = {
        ...rest,
        updatedAt: serverTimestamp(),
      };
      // background_info のサブフィールド部分更新（ドット記法）
      if (backgroundInfoPatch) {
        for (const [key, value] of Object.entries(backgroundInfoPatch)) {
          payload[`background_info.${key}`] = value;
        }
      }
      await safeUpdateDoc(doc(db, 'campaigns', id), payload);
    },
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['campaigns', id] });
    },
  });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      // onCampaignDeleted トリガが各リソースの campaignIds から sweep する
      await safeDeleteDoc(doc(db, 'campaigns', id));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}
